import {
  GET_MEMBERS,
  GET_ORGANISATION,
  setDefaultHeader,
} from '@/studbook/graphql/queries';
import {
  Entity,
  GetMembersResponse,
  GetOrganisationResponse,
  SortDirection,
} from '@/studbook/graphql/results';
import { useLazyQuery, useQuery } from '@apollo/client';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useEffect, useMemo } from 'react';
import {
  ComparisonOperatorText,
  LogicalOperatorText,
} from '@/studbook/constants';
import useMemberDetailFilter from './useMemberDetailFilter';
import useGridStore from '@/studbook/store/useGridStore';
import useMemberFilterStore, {
  DEFAULT_MEMBER_DETAIL,
} from '@/studbook/store/useMemberFilterStore';
import {
  createMemberColumnsFromDetails,
  TRow,
} from '@/components/member/shared/publicSearchMemberHelpers';

const defaultMemberDetail = Object.values(DEFAULT_MEMBER_DETAIL);

function extractDetailValuesMap(r: Entity) {
  let detailValueMap = new Map<string, string | number>();
  let detailIdMap = new Map<string, string | number>();
  let detailHeaderMap = new Map<string, string>();
  r.entityDetailValues.forEach((v) => {
    // let shouldUseAddInfo = [
    //   DEFAULT_ANIMAL_DETAIL.SIRE.toLowerCase(),
    //   DEFAULT_ANIMAL_DETAIL.DAM.toLowerCase(),
    //   DEFAULT_ANIMAL_DETAIL.BREEDER.toLowerCase(),
    //   DEFAULT_ANIMAL_DETAIL.OWNER.toLowerCase(),
    // ].includes(v.entityDetailName.toLowerCase());
    let shouldUseAddInfo = false;
    detailValueMap.set(
      v.entityDetailName,
      shouldUseAddInfo && v.additionalInfo ? v.additionalInfo.name : v.value,
    );
    detailIdMap.set(
      v.entityDetailName,
      shouldUseAddInfo && v.additionalInfo?.id
        ? v.additionalInfo.id
        : v.entityId,
    );
    detailHeaderMap.set(v.entityDetailName, v.entityDetailName);
  });
  return { detailIdMap, detailValueMap };
}

const entityToRowData =
  (columnHeaders: string[], memberDetails?: any) => (r: Entity) => {
    try {
      let { detailIdMap, detailValueMap } = extractDetailValuesMap(r);

      let genericMemberDetail: TRow = {
        [DEFAULT_MEMBER_DETAIL.ID]: {
          id: r.entityId.toString(),
          value: r.entityId,
        },
        [DEFAULT_MEMBER_DETAIL.GUID]: {
          id: r.entityId.toString(),
          value: r.entityGuid,
        },
      };

      // Handle all columns from the member details (both default and dynamic)
      const allPossibleColumns = [
        ...columnHeaders,
        ...(memberDetails?.memberDetails?.map(
          (detail: any) => detail.entityDetailName,
        ) || []),
      ];

      // Remove duplicates and process each unique column
      const uniqueColumns = [...new Set(allPossibleColumns)];

      uniqueColumns.forEach((header) => {
        if (
          header === DEFAULT_MEMBER_DETAIL.ID ||
          header === DEFAULT_MEMBER_DETAIL.GUID
        ) {
          return;
        }
        genericMemberDetail[header] = {
          id: detailIdMap.get(header) ?? '',
          value: detailValueMap.get(header) ?? '',
        };
      });

      return genericMemberDetail;
    } catch (err) {
      console.error(err);
    }

    return {};
  };

export default function useMemberFilter(
  orgId: string,
  paginationModel: { page: number; pageSize: number },
  enabled: boolean,
) {
  const { filters } = useMemberFilterStore();
  const { gridSortModel: sortModel } = useGridStore();
  const {
    data: orgResponse,
    error: errorOrg,
    loading: loadingOrg,
  } = useQuery<GetOrganisationResponse>(GET_ORGANISATION, {
    skip: !orgId,
    variables: {
      orgId: Number(orgId),
    },
    context: {
      headers: {
        ...setDefaultHeader(orgId),
      },
    },
  });
  const { memberDetails } = useMemberDetailFilter(orgId);
  const [getMembers, { data, error, loading, previousData }] =
    useLazyQuery<GetMembersResponse>(GET_MEMBERS);
  const startSearch = () => {
    if (!enabled) return;

    getMembers({
      variables: {
        orgId: Number(orgId),
        currentPage: (paginationModel.page ?? 0) + 1,
        pageSize: paginationModel.pageSize,
        filterInput: {
          filters: filters.map((f) => ({
            entityDetailName: f.detailName,
            value: f.value,
            logicalOperator:
              LogicalOperatorText[f.logicalOperator].toUpperCase(),
            operator:
              ComparisonOperatorText[
                Number(f.operator) as keyof typeof ComparisonOperatorText
              ].toUpperCase(),
          })),
        },
        sortInput: sortModel[0]
          ? {
              entityDetailName: sortModel[0].field,
              sortDirection:
                sortModel[0].sort === 'asc'
                  ? SortDirection.Ascending
                  : SortDirection.Descending,
            }
          : null,
      },
      context: {
        headers: {
          ...setDefaultHeader(orgId),
        },
      },
    });
  };

  useEffect(() => {
    if (!enabled || !memberDetails?.memberDetails) return;
    startSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    memberDetails?.memberDetails,
    filters,
    enabled,
    sortModel,
    paginationModel,
  ]);

  const columns: GridColDef<TRow[][number]>[] = useMemo(
    () =>
      createMemberColumnsFromDetails(
        memberDetails,
        orgResponse?.organisation.name,
      ),
    [memberDetails, orgResponse],
  );

  const rows = useMemo(
    () =>
      loading
        ? []
        : data?.members.results?.map(
            entityToRowData(defaultMemberDetail, memberDetails),
          ),
    [data?.members, loading, memberDetails],
  );

  const getRowId = (row: TRow) => row[DEFAULT_MEMBER_DETAIL.ID].value;

  return {
    data,
    previousData,
    error,
    loading,
    done: (data || error) && !loading,
    dataGrid: {
      columns: columns as unknown as GridColDef<any>[],
      rows: (rows as any) ?? [],
      getRowId,
    },
    startSearch,
  };
}
